import { ProductClient, ProductKitClient } from '@frontend/product/api';
import { CreateProduct, IProduct, ProductType } from '@frontend/product/types';
import { updateField, useAccountRepository } from '@frontend/repository';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../redux/store';

const NAME_TRANSLATION_FIELDS = ['name_nl_be', 'name_en_us', 'name_fr_fr', 'name_de_de'] as const;
const DESCRIPTION_TRANSLATION_FIELDS = ['description_nl_be', 'description_en_us', 'description_fr_fr', 'description_de_de'] as const;
const USER_INFO_TRANSLATION_FIELDS = ['user_info_nl_be', 'user_info_en_us', 'user_info_fr_fr', 'user_info_de_de'] as const;
export const FORM_ID = 'create-kit-modal';

export interface CreateKitModalViewProps {
    initialValue: CreateProduct;
    onSubmit: (v: CreateProduct) => Promise<IProduct>;
    submitDisabled: (v: { [key: string]: any }) => boolean;
    selectedProducts: IProduct[];
    setSelectedProducts: (products: IProduct[]) => void;
    quantities: Record<string, number>;
    setQuantity: (productId: string, quantity: number) => void;
}

export const useCreateKitModal = (): CreateKitModalViewProps => {
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const [selectedProducts, setSelectedProductsState] = useState<IProduct[]>([]);
    const [quantities, setQuantities] = useState<Record<string, number>>({});

    const nameTranslationsModified = useRef<boolean>(false);
    const descriptionTranslationsModified = useRef<boolean>(false);
    const userInfoTranslationsModified = useRef<boolean>(false);

    const previousName = useRef<string>('');
    const previousDescription = useRef<string>('');
    const previousUserInfo = useRef<string>('');

    const form = useAppSelector((state) => state.forms);
    const formValues = useMemo(() => {
        const f = form[FORM_ID];
        if (!f) return {};
        return f.value;
    }, [form]);

    useEffect(() => {
        const currentName = (formValues.name as string) ?? '';
        const currentDescription = (formValues.description as string) ?? '';
        const currentUserInfo = (formValues.user_info as string) ?? '';

        if (!nameTranslationsModified.current) {
            const anyNameTranslationModified = NAME_TRANSLATION_FIELDS.some((field) => {
                const fieldValue = (formValues[field] as string) ?? '';
                const expectedValue = previousName.current;
                return fieldValue !== '' && fieldValue !== expectedValue && fieldValue !== currentName;
            });
            if (anyNameTranslationModified) {
                nameTranslationsModified.current = true;
            }
        }

        if (!descriptionTranslationsModified.current) {
            const anyDescriptionTranslationModified = DESCRIPTION_TRANSLATION_FIELDS.some((field) => {
                const fieldValue = (formValues[field] as string) ?? '';
                const expectedValue = previousDescription.current;
                return fieldValue !== '' && fieldValue !== expectedValue && fieldValue !== currentDescription;
            });
            if (anyDescriptionTranslationModified) {
                descriptionTranslationsModified.current = true;
            }
        }

        if (!userInfoTranslationsModified.current) {
            const anyUserInfoTranslationModified = USER_INFO_TRANSLATION_FIELDS.some((field) => {
                const fieldValue = (formValues[field] as string) ?? '';
                const expectedValue = previousUserInfo.current;
                return fieldValue !== '' && fieldValue !== expectedValue && fieldValue !== currentUserInfo;
            });
            if (anyUserInfoTranslationModified) {
                userInfoTranslationsModified.current = true;
            }
        }

        if (currentName !== previousName.current) {
            if (!nameTranslationsModified.current) {
                NAME_TRANSLATION_FIELDS.forEach((field) => {
                    dispatch(updateField({ formId: FORM_ID, key: field, value: currentName }));
                });
            }
            previousName.current = currentName;
        }

        if (currentDescription !== previousDescription.current) {
            if (!descriptionTranslationsModified.current) {
                DESCRIPTION_TRANSLATION_FIELDS.forEach((field) => {
                    dispatch(updateField({ formId: FORM_ID, key: field, value: currentDescription }));
                });
            }
            previousDescription.current = currentDescription;
        }

        if (currentUserInfo !== previousUserInfo.current) {
            if (!userInfoTranslationsModified.current) {
                USER_INFO_TRANSLATION_FIELDS.forEach((field) => {
                    dispatch(updateField({ formId: FORM_ID, key: field, value: currentUserInfo }));
                });
            }
            previousUserInfo.current = currentUserInfo;
        }
    }, [formValues, dispatch]);

    const onSubmit = async (v: CreateProduct): Promise<IProduct> => {
        const kit = await ProductClient.postProduct(currentAccount, v);

        if (selectedProducts.length > 0) {
            await Promise.all(
                selectedProducts.map((product) =>
                    ProductKitClient.postProductKit(kit.account_id, {
                        kit_id: kit.id,
                        product_id: product.id,
                        quantity: quantities[product.id] ?? 1
                    })
                )
            );
        }

        return kit;
    };

    const submitDisabled = (v: { [key: string]: any }): boolean => {
        return !v.name || v.name === '';
    };

    const setSelectedProducts = (products: IProduct[]) => {
        setSelectedProductsState(products);
        setQuantities((prev) => {
            const next: Record<string, number> = {};
            products.forEach((p) => {
                next[p.id] = prev[p.id] ?? 1;
            });
            return next;
        });
    };

    const setQuantity = (productId: string, quantity: number) => {
        setQuantities((prev) => ({ ...prev, [productId]: Math.max(1, quantity || 1) }));
    };

    return {
        initialValue: {
            type: ProductType.KIT
        } as CreateProduct,
        onSubmit,
        submitDisabled,
        selectedProducts,
        setSelectedProducts,
        quantities,
        setQuantity
    };
};
